import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { EmptyState } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { QueryStates } from "app/pages/sales-intelligence/common-components/QueryStates/QueryStates";
import { BulkDeleteEmployeeProfilesModal } from "app/pages/sales-intelligence/modules/employee-profiles/components/BulkDeleteEmployeeProfilesModal/BulkDeleteEmployeeProfilesModal";
import { DeleteEmployeeProfileModal } from "app/pages/sales-intelligence/modules/employee-profiles/components/DeleteEmployeeProfileModal/DeleteEmployeeProfileModal";
import { EditSharedEmployeeProfileModal } from "app/pages/sales-intelligence/modules/employee-profiles/components/EditSharedEmployeeProfileModal/EditSharedEmployeeProfileModal";
import type { EmployeeProfilesColumnHandlers } from "app/pages/sales-intelligence/modules/employee-profiles/components/EmployeeProfilesTable/columns";
import { EmployeeProfilesTable } from "app/pages/sales-intelligence/modules/employee-profiles/components/EmployeeProfilesTable/EmployeeProfilesTable";
import { EmployeeProfileModalContainer } from "app/pages/sales-intelligence/modules/employee-profiles/containers/EmployeeProfileModalContainer";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { useEmployeeProfilesDeletion } from "../../hooks/useEmployeeProfilesDeletion";
import { useEmployeeProfilesEditing } from "../../hooks/useEmployeeProfilesEditing";
import { useEmployeeProfilesLibrary } from "../../hooks/useEmployeeProfilesLibrary";

import { EmployeeProfilesPageToolbar } from "./EmployeeProfilesPageToolbar";
import { StyledEmployeeProfilesPageContainer } from "./styles";

export const EmployeeProfilesPageContainer = () => {
    const { translate } = useTranslate();

    // Champion is treated like Admin for shared authoring, same convention as Playbooks and Scoring.
    const canManageShared = salesSettingsHelper.isHighLevelAccount();

    const library = useEmployeeProfilesLibrary();
    const deletion = useEmployeeProfilesDeletion();
    const editing = useEmployeeProfilesEditing({ canManageShared });

    const handlers = useMemo<EmployeeProfilesColumnHandlers>(
        () => ({
            onEdit: editing.handleEditEmployeeProfile,
            onDelete: deletion.handleRequestDelete,
        }),
        [editing.handleEditEmployeeProfile, deletion.handleRequestDelete],
    );

    return (
        <StyledEmployeeProfilesPageContainer
            data-automation="employee-profiles-page-container"
            gap="30px"
            margin="0 auto 40px"
            padding="40px 0"
            width="100%"
        >
            <Stack
                flex="0 0 auto"
                gap="12px"
                margin="10px 0"
            >
                <Text
                    fontVariant="h2"
                    color={colorsPalettes.carbon["400"]}
                >
                    {translate("si.employee-profiles.header")}
                </Text>
                <Text
                    fontVariant="body1"
                    color={colorsPalettes.carbon["400"]}
                >
                    {translate("si.employee-profiles.header.subtitle")}
                </Text>
            </Stack>

            <EmployeeProfilesPageToolbar
                canCreateShared={canManageShared}
                sharedCount={library.sharedCount}
                privateCount={library.privateCount}
                onSegmentChange={library.handleSegmentChange}
                searchTerm={library.searchTerm}
                onSearchChange={library.onSearchChange}
                onNewEmployeeProfile={editing.handleNewEmployeeProfile}
            />

            <QueryStates
                isLoading={library.isLoading}
                isError={library.isError}
                onRetry={library.handleRetry}
                errorDataAutomation="employee-profiles-list-error"
                errorRetryDataAutomation="employee-profiles-list-error-retry"
            />

            {library.isLibraryEmpty ? (
                <Flex
                    flex="1"
                    justifyContent="center"
                    width="100%"
                    data-automation="employee-profiles-empty-state"
                    margin="30px auto"
                >
                    <EmptyState
                        title={translate("si.employee-profiles.empty.title")}
                        subtitle={translate("si.employee-profiles.empty.subtitle")}
                    />
                </Flex>
            ) : null}

            {library.hasNoMatches ? (
                <Flex
                    flex="1"
                    justifyContent="center"
                    width="100%"
                    data-automation="employee-profiles-no-results"
                    margin="30px auto"
                >
                    <EmptyState title={translate("si.employee-profiles.search.no_results")} />
                </Flex>
            ) : null}

            {library.showTables ? (
                <Stack gap="30px">
                    {library.showShared ? (
                        <EmployeeProfilesTable
                            key={`shared-${library.segment}`}
                            rows={library.sharedRows}
                            handlers={handlers}
                            dataAutomation="employee-profiles-table-shared"
                            title={translate("si.employee-profiles.section.shared.title")}
                            subtitle={translate("si.employee-profiles.section.shared.subtitle")}
                            showCreatorName
                            canManageRows={canManageShared}
                            isDeletingSelected={deletion.isDeleting}
                            clearSelectionToken={deletion.clearSelectionTokens.shared}
                            onDeleteSelected={(ids) => deletion.handleRequestBulkDelete(ids, true)}
                        />
                    ) : null}

                    {library.showPrivate ? (
                        <EmployeeProfilesTable
                            key={`private-${library.segment}`}
                            rows={library.privateRows}
                            handlers={handlers}
                            dataAutomation="employee-profiles-table-private"
                            title={translate("si.employee-profiles.section.private.title")}
                            subtitle={translate("si.employee-profiles.section.private.subtitle")}
                            canManageRows
                            isDeletingSelected={deletion.isDeleting}
                            clearSelectionToken={deletion.clearSelectionTokens.private}
                            onDeleteSelected={(ids) => deletion.handleRequestBulkDelete(ids, false)}
                        />
                    ) : null}
                </Stack>
            ) : null}

            <EditSharedEmployeeProfileModal
                isOpen={editing.isSharedEditModalOpen}
                onCancel={editing.handleCancelSharedEdit}
                onConfirm={editing.handleConfirmSharedEdit}
            />

            <DeleteEmployeeProfileModal
                isOpen={deletion.isDeleteModalOpen}
                isShared={deletion.isProfileToDeleteShared}
                profileName={deletion.profileToDeleteName}
                isDeleting={deletion.isDeleting}
                onCancel={deletion.handleCancelDelete}
                onConfirm={() => void deletion.handleConfirmDelete()}
            />

            <BulkDeleteEmployeeProfilesModal
                isOpen={deletion.isBulkDeleteModalOpen}
                isShared={deletion.isBulkDeleteShared}
                count={deletion.bulkDeleteCount}
                isDeleting={deletion.isDeleting}
                onCancel={deletion.handleCancelBulkDelete}
                onConfirm={() => void deletion.handleConfirmBulkDelete()}
            />

            <EmployeeProfileModalContainer
                isOpen={editing.isProfileModalOpen}
                isSharedWithAccount={editing.isCreatingShared}
                profileForEdit={editing.profileForEdit}
                onClose={editing.handleCloseProfileModal}
            />
        </StyledEmployeeProfilesPageContainer>
    );
};
